import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModuleAccess } from '../organizationStandards/useModuleAccess';
import { fetchSgDashboard } from './sgDashboardApi';
import { SgKpiCard } from './SgKpiCard';
import { buildSgDashboardCards } from './sgDashboardUtils';
import type { SgDashboardData } from './types';

function formatPeriodDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function SgDashboardView() {
  const { canAccess, ready } = useModuleAccess();
  const [data, setData] = useState<SgDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboard = await fetchSgDashboard();
      setData(dashboard);
    } catch {
      setError('No se pudo cargar el dashboard del Sistema de Gestión.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleCards = useMemo(() => {
    if (!data) return [];
    return buildSgDashboardCards(data).filter((card) => canAccess(card.permission));
  }, [data, canAccess]);

  if (!ready || loading) {
    return (
      <p className='text-sm text-muted-foreground' data-testid='sg-dashboard-loading'>
        Cargando estado del Sistema de Gestión…
      </p>
    );
  }

  if (error) {
    return (
      <div className='rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className='space-y-6' data-testid='sg-dashboard'>
      <div>
        <h1 className='text-2xl font-bold text-foreground'>Dashboard del Sistema de Gestión</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Período: {formatPeriodDate(data.period.from)} — {formatPeriodDate(data.period.to)}
        </p>
      </div>

      <section className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4'>
        {visibleCards.map((card) => (
          <SgKpiCard
            key={card.id}
            title={card.title}
            to={card.to}
            primaryLabel={card.primaryLabel}
            primaryValue={card.primaryValue}
            secondaryLabel={card.secondaryLabel}
            secondaryValue={card.secondaryValue}
            percentLabel={card.percentLabel}
            percentValue={card.percentValue}
            level={card.level}
          />
        ))}
      </section>

      {visibleCards.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          No hay módulos visibles con los permisos y normas habilitadas de su organización.
        </p>
      )}
    </div>
  );
}
